import { formatPrice } from './misc';

/**
 * Calculate the total savings from regular and bulk discounts
 * @param {Object} item - Cart item
 * @returns {number} - Total savings amount
 */
export const calculateSavings = (item) => {
  let totalSavings = 0;

  // Calculate regular sale savings
  if (item.sale_price && item.regular_price) {
    const regularPrice = parseFloat(item.regular_price) || 0;
    const salePrice = parseFloat(item.sale_price) || item.price;
    const savingsPerItem = Math.max(regularPrice - salePrice, 0);
    totalSavings += savingsPerItem * item.quantity;
  }

  // Calculate bulk sale savings
  if (item.bulk_discount_rules) {
    const buyXGetXRule = item.bulk_discount_rules.find(rule => rule.type === 'buy_x_get_x');
    if (buyXGetXRule && buyXGetXRule.buy_x_get_x_products) {
      const productRule = buyXGetXRule.buy_x_get_x_products.find(rule => rule.product_id === item.id);
      if (productRule) {
        const { buy_quantity, get_quantity, discount_type, discount_value } = productRule;
        const regularPrice = parseFloat(item.regular_price) || 0;

        // Calculate how many complete sets of buy_quantity + get_quantity the user has
        const completeSets = Math.floor(item.quantity / (buy_quantity + get_quantity));

        if (discount_type === 'percentage') {
          const savingsPerSet = (regularPrice * get_quantity) * (discount_value / 100);
          totalSavings += savingsPerSet * completeSets;
        } else if (discount_type === 'fixed') {
          const savingsPerSet = discount_value * get_quantity;
          totalSavings += savingsPerSet * completeSets;
        } else if (discount_type === 'fixed_price') {
          const savingsPerSet = (regularPrice - discount_value) * get_quantity;
          totalSavings += savingsPerSet * completeSets;
        }
      }
    }
  }

  return totalSavings;
};

/**
 * Calculate the total price with discounts applied
 * @param {Object} item - Cart item
 * @returns {number} - Total price with discounts
 */
export const calculateTotalPrice = (item) => {
  const regularPrice = parseFloat(item.regular_price) || 0;
  const salePrice = parseFloat(item.sale_price) || regularPrice;
  const quantity = item.quantity;

  // If there's a regular sale, use that
  if (item.sale_price && item.regular_price) {
    return salePrice * quantity;
  }

  // If there's a bulk discount, calculate based on sets
  if (item.bulk_discount_rules) {
    const buyXGetXRule = item.bulk_discount_rules.find(rule => rule.type === 'buy_x_get_x');
    if (buyXGetXRule && buyXGetXRule.buy_x_get_x_products) {
      const productRule = buyXGetXRule.buy_x_get_x_products.find(rule => rule.product_id === item.id);
      if (productRule) {
        const { buy_quantity, get_quantity, discount_type, discount_value } = productRule;

        // Calculate complete sets and remaining items
        const completeSets = Math.floor(quantity / (buy_quantity + get_quantity));
        const remainingItems = quantity % (buy_quantity + get_quantity);

        // Calculate price for complete sets
        let setPrice = 0;
        if (discount_type === 'percentage') {
          setPrice = (regularPrice * buy_quantity) + (regularPrice * get_quantity * (1 - discount_value / 100));
        } else if (discount_type === 'fixed') {
          setPrice = (regularPrice * buy_quantity) + (regularPrice * get_quantity - discount_value * get_quantity);
        } else if (discount_type === 'fixed_price') {
          setPrice = (regularPrice * buy_quantity) + (discount_value * get_quantity);
        } else {
          setPrice = regularPrice * (buy_quantity + get_quantity);
        }

        // Add price for complete sets and remaining items
        return (setPrice * completeSets) + (regularPrice * remainingItems);
      }
    }
  }

  // If no discounts apply, use regular price
  return regularPrice * quantity;
};

/**
 * Get the bulk sale rule for a product if it exists
 * @param {Object} product - Product object
 * @returns {Object|null} - Bulk sale rule or null if none exists
 */
export const getBulkSaleRule = (product) => {
  if (!product.bulk_discount_rules) return null;

  const buyXGetXRule = product.bulk_discount_rules.find(rule => rule.type === 'buy_x_get_x');
  if (!buyXGetXRule || !buyXGetXRule.buy_x_get_x_products) return null;

  const productRule = buyXGetXRule.buy_x_get_x_products.find(rule => rule.product_id === product.id);
  if (!productRule) return null;

  return {
    ...productRule,
    setSize: productRule.buy_quantity + productRule.get_quantity,
  };
};

/**
 * Calculate the potential savings for a bulk sale
 * @param {Object} rule - Bulk sale rule
 * @param {number} regularPrice - Regular price of the product
 * @returns {number} - Potential savings amount
 */
export const calculatePotentialSavings = (rule, regularPrice) => {
  const { discount_type, discount_value, get_quantity } = rule;

  if (discount_type === 'percentage') {
    return (regularPrice * get_quantity) * (discount_value / 100);
  } else if (discount_type === 'fixed') {
    return discount_value * get_quantity;
  } else if (discount_type === 'fixed_price') {
    return (regularPrice - discount_value) * get_quantity;
  }

  return 0;
};

/**
 * Calculate the total price for a complete set in a bulk sale
 * @param {Object} rule - Bulk sale rule
 * @param {number} regularPrice - Regular price of the product
 * @returns {number} - Total price for the set
 */
export const calculateSetPrice = (rule, regularPrice) => {
  const { buy_quantity, get_quantity, discount_type, discount_value } = rule;

  // Add full price for "buy" items
  let totalPrice = buy_quantity * regularPrice;

  // Add discounted price for "get" items
  if (discount_type === 'percentage') {
    const discountedPrice = regularPrice * (1 - (discount_value / 100));
    totalPrice += get_quantity * discountedPrice;
  } else if (discount_type === 'fixed') {
    const discountedPrice = Math.max(0, regularPrice - discount_value);
    totalPrice += get_quantity * discountedPrice;
  } else if (discount_type === 'fixed_price') {
    totalPrice += get_quantity * discount_value;
  } else {
    totalPrice += get_quantity * regularPrice;
  }

  return totalPrice;
};

/**
 * Generate bulk sale info for display in components
 * @param {Object} item - Cart item
 * @param {Function} t - Translation function
 * @param {Object} colors - UI colors
 * @returns {Object|null} - Bulk sale info for rendering
 */
export const getBulkSaleInfo = (item, t, colors) => {
  const rule = getBulkSaleRule(item);
  if (!rule) return null;

  const regularPrice = parseFloat(item.regular_price) || 0;
  const { setSize } = rule;

  // Calculate complete sets and remaining items
  const completeSets = Math.floor(item.quantity / setSize);
  const remainingItems = item.quantity % setSize;

  // Calculate savings per set
  const savingsPerSet = calculatePotentialSavings(rule, regularPrice);

  // Calculate remaining items needed for next set
  const remainingToQualify = setSize - remainingItems;

  // Current savings from complete sets
  const currentSavings = savingsPerSet * completeSets;

  // Determine if we have at least one complete set
  const hasCompleteSets = completeSets > 0;

  // Determine if we have remaining items that could form another set
  const hasPartialNextSet = remainingItems > 0;

  return {
    rule,
    regularPrice,
    completeSets,
    remainingItems,
    savingsPerSet,
    remainingToQualify,
    currentSavings,
    hasCompleteSets,
    hasPartialNextSet,
    formattedCurrentSavings: formatPrice(currentSavings),
    formattedPotentialSavings: formatPrice(savingsPerSet),
    tagText: hasCompleteSets ? t('saved') : t('sale_available'),
    tagBgColor: colors.secondaryColor || '#000',
    tagTextColor: '#FFFFFF',
    bgColor: colors.secondaryColor ? colors.secondaryColor : '#000',
    textColor: colors.secondaryTextColor || '#666',
  };
};